import { useEffect, useState } from 'react';
import { useUserPermissions, usePutUserPermissions } from '../../hooks/useRoles';
import type { Person } from '../../lib/peopleApi';
import {
  emptyMatrix,
  type RoleTemplate,
  type UserPermissionMatrix,
} from '../../lib/permissionsApi';
import { RbacMatrixEditor } from '../rbac/RbacMatrixEditor';

/**
 * Settings > Team Management > per-user permissions editor (CLE-216). Loads a
 * specific user's stored matrix + attached template, lets an admin pick a
 * template (pre-filling the matrix) and/or apply custom overrides, then saves
 * via PUT /api/users/:id/permissions.
 */
export function UserPermissionsEditor({
  person,
  templates,
  onDone,
}: {
  person: Person;
  templates: RoleTemplate[];
  onDone: () => void;
}) {
  const userId = person.userId as string;
  const { data, isLoading } = useUserPermissions(userId);
  const put = usePutUserPermissions();

  const [matrix, setMatrix] = useState<UserPermissionMatrix>(emptyMatrix());
  const [templateId, setTemplateId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate local state once the user's stored permissions load.
  useEffect(() => {
    if (data && !hydrated) {
      setMatrix(JSON.parse(JSON.stringify(data.permissions)) as UserPermissionMatrix);
      setTemplateId(data.templateId ?? '');
      setHydrated(true);
    }
  }, [data, hydrated]);

  function handleSave() {
    setError(null);
    put.mutate(
      { userId, permissions: matrix, roleTemplateId: templateId || null },
      {
        onSuccess: onDone,
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Salvarea permisiunilor a eșuat'),
      },
    );
  }

  return (
    <div className="max-w-4xl">
      <button type="button" onClick={onDone} className="text-sm text-brand-600 hover:underline">
        ← Înapoi la echipă
      </button>
      <h3 className="mt-3 text-lg font-semibold text-gray-900">
        Permisiuni pentru {person.fullName}
      </h3>
      <p className="mt-1 text-sm text-gray-500">{person.email}</p>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă permisiunile...</p>}

      {hydrated && (
        <div className="mt-4">
          <RbacMatrixEditor
            value={matrix}
            onChange={setMatrix}
            templates={templates}
            templateId={templateId}
            onSelectTemplate={(id) => setTemplateId(id ?? '')}
          />
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onDone} className="ui-btn-secondary">
          Anulează
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={put.isPending || !hydrated}
          className="ui-btn-primary"
        >
          {put.isPending ? 'Se salvează...' : 'Salvează permisiunile'}
        </button>
      </div>
    </div>
  );
}
